import { useEffect, useState } from "react";
import { advisoryApi } from "../advisoryApi";
import { dateTime } from "../format";
import { useSaver, useStepLoad, type StepProps } from "./common";

interface NotesData {
  notes: { flowPageId: string; goalId: string | null; notes: string; updatedAt: string }[];
}

const MAX = 20000;

/**
 * The advisor's note for this page (QP: advisor-notes-editor under the page).
 * Optional unless the page requires one; never gates submit otherwise. Saved
 * explicitly with its own quiet button so the page keeps one primary action.
 */
export function AdvisorNoteStep({
  sessionId,
  addr,
  readOnly,
  onSaved,
  required,
}: Pick<StepProps, "sessionId" | "addr" | "readOnly" | "onSaved"> & { required: boolean }) {
  const noteAddr = { pageId: addr.pageId, goalId: addr.goalId };
  const load = useStepLoad<NotesData>(sessionId, "advisor-note", noteAddr);
  const saver = useSaver(onSaved);
  const [text, setText] = useState("");
  const [baseline, setBaseline] = useState("");
  const [open, setOpen] = useState(required);

  const current = load.data?.notes.find((n) => (addr.goalId ? n.goalId === addr.goalId : !n.goalId)) ?? load.data?.notes[0];

  useEffect(() => {
    const t = current?.notes ?? "";
    setText(t);
    setBaseline(t);
    if (t) setOpen(true);
  }, [current?.notes]);

  if (!addr.pageId) return null;
  const dirty = text !== baseline;

  async function save() {
    const r = await saver.run(() =>
      advisoryApi.saveStep(sessionId, "advisor-note", noteAddr, {
        notes: text,
        pageId: addr.pageId!,
        ...(addr.goalId ? { goalId: addr.goalId } : {}),
      }),
    );
    if (r) setBaseline(text);
  }

  if (!open && !readOnly) {
    return (
      <button type="button" className="link-action adv-note-toggle" onClick={() => setOpen(true)}>
        + Add an advisor note for this page
      </button>
    );
  }
  if (readOnly && !baseline) return null;

  return (
    <section className="adv-note">
      <div className="adv-note-head">
        <span className="adv-label">Advisor note{required ? " *" : " · optional"}</span>
        {current?.updatedAt && <span className="muted">Saved {dateTime(current.updatedAt)}</span>}
      </div>
      {load.error && <div className="banner-error">{load.error}</div>}
      <textarea
        className="adv-textarea"
        rows={4}
        maxLength={MAX}
        value={text}
        placeholder="What was discussed, what the client said, why you recommend this…"
        onChange={(e) => setText(e.target.value)}
        disabled={readOnly || load.loading}
      />
      {!readOnly && (
        <div className="adv-note-actions">
          {saver.error && <span className="adv-inline-error">{saver.error}</span>}
          <button type="button" className="btn btn-ghost adv-btn" onClick={save} disabled={!dirty || saver.saving}>
            {saver.saving ? "Saving…" : dirty ? "Save note" : "Note saved"}
          </button>
        </div>
      )}
    </section>
  );
}
